<template>
  <div class="container px-0 mb-5">
    <the-header />

    <b-card class="border-0 radius-top-0 mb-5" body-class="pt-0">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="text-muted font-weight-medium h6 mb-0">Detalhes do plano de ação</div>
        <b-badge v-if="statusTitle" pill :variant="statusVariant" class="font-weight-normal">
          {{ statusTitle }}
        </b-badge>
      </div>

      <b-alert v-if="!state.loading && !state.form.id" show variant="warning" class="mb-3">
        Não foi possível localizar este plano de ação.
      </b-alert>

      <div v-else>
        <action-base-details :list-details="normalizedPlan" :show-title="false" />

        <div v-if="planActions.length" class="sub-actions-section mt-4">
          <div class="d-flex align-items-center mb-3">
            <b-icon icon="diagram3" class="mr-2 text-primary" />
            <span class="text-muted font-weight-medium h6 mb-0">
              Ações ({{ planActions.length }})
            </span>
          </div>
          <div
            v-for="action in planActions"
            :key="action.id"
            class="sub-action-detail-card"
          >
            <div class="d-flex align-items-center justify-content-between mb-2">
              <b-badge
                pill
                :variant="getStatusColor(action.status)"
                class="font-weight-normal"
              >
                {{ getStatusTitle(action.status) }}
              </b-badge>
              <b-button
                size="sm"
                pill
                variant="outline-primary"
                @click="viewAction(action)"
              >
                <b-icon icon="eye" class="mr-1" />
                Ver
              </b-button>
            </div>
            <action-base-details :list-details="normalizeAction(action)" :show-title="false" />
          </div>
        </div>

        <div class="d-flex justify-content-between mt-4">
          <b-button variant="outline-secondary" @click="goBack">Voltar</b-button>
          <b-button variant="primary" @click="goToEdit">
            <b-icon icon="pencil-square" class="mr-1" />
            Editar
          </b-button>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive } from "vue";

import TheHeader from "@/modules/action_plans/components/TheHeader.vue";
import ActionBaseDetails from "@/modules/action_plans/components/ActionBaseDetails.vue";

import { useMapActions } from "@/composables/useMapActions";
import { useMapGetters } from "@/composables/useMapGetters";
import { useRoute } from "@/composables/useRoute";
import { useRouter } from "@/composables/useRouter";

const route = useRoute();
const router = useRouter();

const state = reactive({
  loading: false,
  form: {},
  allItems: [],
});

const idPlan = computed(() => route.params.id);

const { ActionGetOnePlan, ActionGetAllItems } = useMapActions("action_plans", [
  "ActionGetOnePlan",
  "ActionGetAllItems",
]);
const { GetMappedStatus, GetStatusKey } = useMapGetters("action_plans", ["GetMappedStatus", "GetStatusKey"]);
const mappedStatus = GetMappedStatus();
const getStatusKey = GetStatusKey();

function goBack() {
  window.history.back();
}

function goToEdit() {
  router.push({ name: "edit_action_plan", params: { id: idPlan.value } });
}

function mapPlanToActionForm(plan) {
  if (!plan) return {};
  return {
    ...plan,
    what: plan.what ?? plan.name ?? "",
    who_id: plan.who_id ?? plan.model_id ?? "",
    who_type: plan.who_type ?? plan.model_type ?? "organizational_structure",
    action_plans_how_much: plan.action_plans_how_much ?? [],
  };
}

const normalizedPlan = computed(() => {
  const item = state.form || {};
  return {
    ...item,
    what: item.what ?? item.name ?? "",
    why: item.why ?? "",
    where: item.where ?? "",
    who: item.who?.name ?? item.who ?? "",
    when_start: item.when_start ?? item.when ?? item.whenStart ?? "",
    how: item.how ?? "",
    how_much: item.how_much ?? item.howMuch ?? item.cost ?? "",
  };
});

const planActions = computed(() => {
  const items = state.allItems || [];
  return items.filter((i) => !i.parent_id);
});

const statusKey = computed(() => getStatusKey(normalizedPlan.value?.status));
const statusTitle = computed(() => mappedStatus[statusKey.value]?.title || "");
const statusVariant = computed(() => mappedStatus[statusKey.value]?.color || "secondary");

function getStatusTitle(status = "") {
  const key = getStatusKey(status);
  return mappedStatus[key]?.title ?? "Aguardando";
}

function getStatusColor(status = "") {
  const key = getStatusKey(status);
  return mappedStatus[key]?.color ?? "secondary";
}

function normalizeAction(action) {
  const item = action || {};
  return {
    ...item,
    what: item.what ?? item.name ?? "",
    why: item.why ?? "",
    where: item.where ?? "",
    who: item.who?.name ?? item.who ?? "",
    when_start: item.when_start ?? item.when ?? item.whenStart ?? "",
    how: item.how ?? "",
    how_much: item.how_much ?? item.howMuch ?? item.cost ?? "",
  };
}

function viewAction(action) {
  router.push({
    name: "show_action_plan_item",
    params: { id: idPlan.value, itemId: action.id },
  });
}

async function loadData() {
  if (!idPlan.value) return;
  state.loading = true;
  try {
    const result = await ActionGetOnePlan({ id: idPlan.value });
    state.form = mapPlanToActionForm(result?.action_plan || {});
    const itemsResult = await ActionGetAllItems({ id: idPlan.value });
    const items = Array.isArray(itemsResult?.data) ? itemsResult.data : [];
    state.allItems = items;
  } catch (e) {
    state.form = {};
    state.allItems = [];
  } finally {
    state.loading = false;
  }
}

onMounted(() => {
  loadData();
});
</script>

<style scoped lang="scss">
.sub-actions-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}
.sub-action-detail-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #f8f9fa;
}
.sub-action-detail-card:last-child {
  margin-bottom: 0;
}
</style>
